import { useState } from 'react';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Form, FormActions } from '@/components/forms/Form';
import { FormInput } from '@/components/forms/FormField';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { apiClient } from '@/lib/api-client';

// Schema for requesting a password reset
const resetRequestSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ResetRequestFormValues = z.infer<typeof resetRequestSchema>;

// Schema for setting a new password (Requirement 2.5: 8-128 chars, uppercase, lowercase, digit, special)
const newPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password must be at most 128 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/\d/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type NewPasswordFormValues = z.infer<typeof newPasswordSchema>;

/**
 * Password reset page with two modes:
 * 1. Request mode: User enters email to receive a reset link
 * 2. Reset mode: User enters new password (accessed via reset link with token)
 *
 * Validates: Requirement 2.8
 */
export function PasswordReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (token) {
    return <NewPasswordForm token={token} />;
  }

  return <ResetRequestForm />;
}

/**
 * Form to request a password reset email.
 */
function ResetRequestForm() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestMutation = useMutation({
    mutationFn: async (email: string) => {
      const tenantId = import.meta.env.VITE_DEFAULT_TENANT_ID;
      await apiClient.post('/auth/password/reset-request', { email, tenantId });
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: () => {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    },
  });

  function handleSubmit(values: ResetRequestFormValues) {
    setError(null);
    requestMutation.mutate(values.email);
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 dark:bg-surface-dark">
        <Card className="w-full max-w-md" padding="lg">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
              <svg
                className="h-6 w-6 text-brand-600 dark:text-brand-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Check Your Email</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              If an account exists with that email address, we've sent a password reset link. The
              link will expire in 60 minutes.
            </p>
            <div className="mt-6">
              <Link
                to="/auth/login"
                className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 dark:bg-surface-dark">
      <Card className="w-full max-w-md" padding="lg">
        <CardHeader
          title="Reset Your Password"
          description="Enter your email address and we'll send you a link to reset your password."
        />
        <CardContent>
          {error && (
            <div
              className="mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-400"
              role="alert"
            >
              {error}
            </div>
          )}

          <Form<ResetRequestFormValues>
            schema={resetRequestSchema}
            defaultValues={{ email: '' }}
            onSubmit={handleSubmit}
          >
            {(form) => (
              <>
                <FormInput
                  name="email"
                  control={form.control}
                  label="Email Address"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
                <FormActions className="flex-col items-stretch gap-4">
                  <Button type="submit" loading={requestMutation.isPending} className="w-full">
                    Send Reset Link
                  </Button>
                </FormActions>
              </>
            )}
          </Form>

          <div className="mt-4 text-center">
            <Link
              to="/auth/login"
              className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
            >
              Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Form to set a new password using a reset token.
 */
function NewPasswordForm({ token }: { token: string }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetMutation = useMutation({
    mutationFn: async (password: string) => {
      await apiClient.post('/auth/password/reset', { token, newPassword: password });
    },
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (err) => {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as {
          response?: { data?: { error?: { code?: string; message?: string } } };
        };
        const errorCode = axiosError.response?.data?.error?.code;
        if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'TOKEN_INVALID') {
          setError('This reset link has expired or is invalid. Please request a new one.');
        } else {
          setError(
            axiosError.response?.data?.error?.message ||
              'Failed to reset password. Please try again.',
          );
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    },
  });

  function handleSubmit(values: NewPasswordFormValues) {
    setError(null);
    resetMutation.mutate(values.password);
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 dark:bg-surface-dark">
        <Card className="w-full max-w-md" padding="lg">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <svg
                className="h-6 w-6 text-green-600 dark:text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Password Reset Successful
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <div className="mt-6">
              <Button onClick={() => navigate('/auth/login')} className="w-full">
                Sign In
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 dark:bg-surface-dark">
      <Card className="w-full max-w-md" padding="lg">
        <CardHeader title="Set New Password" description="Enter your new password below." />
        <CardContent>
          {error && (
            <div
              className="mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-400"
              role="alert"
            >
              {error}
            </div>
          )}

          <Form<NewPasswordFormValues>
            schema={newPasswordSchema}
            defaultValues={{ password: '', confirmPassword: '' }}
            onSubmit={handleSubmit}
          >
            {(form) => (
              <>
                <FormInput
                  name="password"
                  control={form.control}
                  label="New Password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  hint="8-128 characters with uppercase, lowercase, number, and special character"
                  required
                />
                <FormInput
                  name="confirmPassword"
                  control={form.control}
                  label="Confirm Password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                />
                <FormActions className="flex-col items-stretch gap-4">
                  <Button type="submit" loading={resetMutation.isPending} className="w-full">
                    Reset Password
                  </Button>
                </FormActions>
              </>
            )}
          </Form>

          <div className="mt-4 text-center">
            <Link
              to="/auth/login"
              className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
            >
              Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
